import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {BankAccount} from '../types';

// FNP-918: was `tables/columns.tsx` — a bare array where EVERY column was
// `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible — you
// cannot add `sortable: true` to a column that has nothing to sort on. Moved
// into a hook to match the platform shape (checklist §3z).
//
// ⚠️ `accountNumber` is NOT a column, and that is deliberate. It exists on the
// BankAccount type but no query selects it — see queries.ts, which fetches
// routingNumber and bankName only. A value not fetched cannot leak (§11), so
// adding a column for it would mean widening the query first. Do not.
export const useBankAccountsTableColumns = () => {

    const columns: DataTableColumn<BankAccount>[] = [
        {
            accessor: 'id',
            sortable: true,
            title: I18n.get('Bank Account'),
            width: '1fr',
            // Long ids in monospace (§13). Replaces a hand-rolled <span> with
            // an inline colour and an onClick — not keyboard-reachable, no
            // focus ring, and a hardcoded colour instead of a theme token.
            // Row click now navigates; RecordsTable owns `onRowClick`.
            render: (item: BankAccount) => (
                <span style={{fontFamily: 'monospace'}}>{item.id}</span>
            ),
        },
        {
            accessor: 'accountHolderName',
            sortable: true,
            title: I18n.get('Account Holder'),
            width: '1fr',
            // Nullable fields render the em-dash placeholder, never blank or
            // `undefined` (§13). Nulls sort LAST in both directions — that is
            // sortRecords' rule, not something this column configures.
            render: (item: BankAccount) => item.accountHolderName ?? '—',
        },
        {
            accessor: 'bankName',
            sortable: true,
            title: I18n.get('Bank'),
            width: '1fr',
            render: (item: BankAccount) => item.bankName ?? '—',
        },
        {
            accessor: 'routingNumber',
            sortable: true,
            title: I18n.get('Routing Number'),
            width: '1fr',
            // A routing number identifies the BANK, not the account — it is
            // public information (the ABA directory publishes it) and is what
            // an operator matches against a customer's statement. The account
            // number, which is the sensitive half, is never fetched.
            render: (item: BankAccount) => item.routingNumber
                ? (
                    <span style={{fontFamily: 'monospace'}}>
                        {item.routingNumber}
                    </span>
                )
                : '—',
        },
        {
            accessor: 'currency',
            sortable: true,
            title: I18n.get('Currency'),
            width: '1fr',
            // Uppercased for display: the API returns ISO codes lowercase
            // ("usd"), which reads as a typo next to the country code.
            render: (item: BankAccount) => item.currency
                ? item.currency.toUpperCase()
                : '—',
        },
        {
            accessor: 'created',
            sortable: true,
            title: I18n.get('Created'),
            width: '1fr',
            // formatTimestamp guards null/unparseable and renders the em-dash;
            // an unguarded moment(undefined) would render RIGHT NOW as fact.
            render: (item: BankAccount) => formatTimestamp(item.created),
        },
    ];

    return {columns};
};
